import { useEditorStore } from "../stores/use-editor-store";
import { Region } from "@/api/studio/data/types/region.type";
import { toast } from "@/components/ui/toast";
import { ClassificationList } from "../components/classification-list";
import { Classification } from "@/api/studio/data/types/classification.type";
import { useSegment } from "@/hooks/use-segment";
import { getUniqueLabel } from "@/features/studies/utils/get-unique-label";
import { classNames } from "@/utils/classification-mapping";

/* Prop Types */
interface UpdateRegionProps {
  region: Region;
  onClose: () => void;
  projectId: string; // used for segment tracking
  studyId: string; // used for segment tracking
}
/*
 * UpdateRegionModal component
 * This component is used to update the classification of a region
 */
export const UpdateRegionModal = ({ region, onClose, projectId, studyId }: UpdateRegionProps) => {
  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Actions */
  const { updateRegionClassification, updateRegionLabel } = useEditorStore((state) => state.editorActions);

  /* Stores */
  const sidePanelDisplay = useEditorStore((state) => state.sidePanelDisplay);

  /* Handlers */
  const handleClassificationUpdate = (value: string) => {
    const classification = value as Classification;
    // Segment tracking
    segmentTrackEvent("AOI Classification Changed", {
      from: region.classification,
      to: classification,
      projectId,
      studyId,
      studyType: "asset",
    });

    // Ensure unique label when changing classification
    const displayLabel = classNames[classification] || classification;
    const existingLabels = sidePanelDisplay.map((r) => r.label);
    const uniqueLabel = getUniqueLabel(displayLabel, existingLabels, region.label);

    // Update the region classification and label
    updateRegionClassification(region.id, classification);
    updateRegionLabel(region.id, uniqueLabel);

    // Toast
    toast({
      title: "Classification Updated",
      type: "success",
    });
    onClose();
  };

  /* Render */
  return (
    <div className="grid grid-cols-1 gap-2">
      <ClassificationList handleClassificationSelect={handleClassificationUpdate} studyType="asset" />
    </div>
  );
};
